import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/** Mirrors ResellerStatus in src/Platform.Domain/Enums.cs. */
export const ResellerStatus = {
  Pending: 0,
  Active: 1,
  Suspended: 2,
} as const;

export const resellerStatusLabels: Record<number, string> = {
  0: 'Pending',
  1: 'Active',
  2: 'Suspended',
};

/** Suspended is the one that stops business, so it is the one that reads as a problem. */
export const resellerStatusColors: Record<number, string> = {
  0: 'blue',
  1: 'green',
  2: 'red',
};

/** Mirrors ResellerPayoutsStatus in src/Platform.Domain/Entities/Reseller.cs. */
export const PayoutsStatus = {
  NotOnboarded: 0,
  Pending: 1,
  Active: 2,
  Restricted: 3,
} as const;

export const payoutsStatusLabels: Record<number, string> = {
  0: 'Not onboarded',
  1: 'Onboarding',
  2: 'Ready',
  3: 'Restricted',
};

export const payoutsStatusColors: Record<number, string> = {
  0: 'gray',
  1: 'orange',
  2: 'green',
  3: 'red',
};

/** Mirrors CommissionStatus in src/Platform.Domain/Entities/Commission.cs. */
export const CommissionStatus = {
  Accrued: 0,
  Transferred: 1,
  Reversed: 2,
  Blocked: 3,
} as const;

export const commissionStatusLabels: Record<number, string> = {
  0: 'Owed',
  1: 'Paid',
  2: 'Reversed',
  3: 'Held',
};

/**
 * Held is orange rather than red: the money is owed and will move, it just cannot yet. Red would
 * read as "something is broken" for what is usually a partner halfway through onboarding.
 */
export const commissionStatusColors: Record<number, string> = {
  0: 'blue',
  1: 'green',
  2: 'gray',
  3: 'orange',
};

export interface Reseller {
  id: string;
  name: string;
  slug: string;
  status: number;
  marginBasisPoints: number;
  billingEmail: string;
  payoutsStatus: number;
  hasStripeCustomer: boolean;
  hasConnectedAccount: boolean;
  tenantCount: number;
  createdAt: string;
  /** Set on create when the Connect invite email was sent. */
  invited?: boolean;
}

export interface PartnerConnectInviteResult {
  invited: boolean;
  inviteUrl: string;
  alreadyOnboarded: boolean;
}

export interface CommissionEntry {
  id: string;
  earnedByTenantId: string;
  tenantName: string | null;
  stripeInvoiceId: string;
  paidMinorUnits: number;
  rateBasisPoints: number;
  commissionMinorUnits: number;
  currency: string;
  status: number;
  transferredAt: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  lastError: string | null;
  createdAt: string;
}

export interface CommissionStatement {
  entries: CommissionEntry[];
  owedMinorUnits: number;
  paidMinorUnits: number;
  reversedMinorUnits: number;
  /** Null when the entries span several currencies, in which case the totals do not add up. */
  currency: string | null;
}

/**
 * A margin as a percentage, for display only.
 *
 * Basis points everywhere else: the server holds 1500 and divides nowhere, because a margin held as
 * a float is a rounding error waiting to reach an invoice. This is the one place it becomes a
 * percentage, and only to be read.
 */
export function formatMargin(basisPoints: number): string {
  return `${(basisPoints / 100).toFixed(basisPoints % 100 === 0 ? 0 : 2)}%`;
}

/** Partners, alphabetically. Platform-level: a partner belongs to no tenant. */
export function useResellers() {
  return useQuery({
    queryKey: ['resellers'],
    queryFn: () => api.get<Reseller[]>('/api/resellers'),
  });
}

export function useCreateReseller() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      name: string;
      slug: string;
      billingEmail: string;
      marginBasisPoints: number;
    }) => api.post<Reseller>('/api/resellers', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['resellers'] }),
  });
}

export function useUpdateReseller(resellerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      name?: string;
      billingEmail?: string;
      marginBasisPoints?: number;
      status?: number;
    }) => api.patch<Reseller>(`/api/resellers/${resellerId}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['resellers'] }),
  });
}

/**
 * A partner's statement.
 *
 * Only fetched when the drawer is open: a statement is a page of history nobody is watching change,
 * and loading every partner's on the list screen would be a lot of reading for a column of totals.
 */
export function useCommissionStatement(resellerId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['resellers', resellerId, 'commissions'],
    queryFn: () => api.get<CommissionStatement>(`/api/resellers/${resellerId}/commissions`),
    enabled: (options?.enabled ?? true) && resellerId.length > 0,
  });
}

/**
 * Ensures the partner has a Stripe Connect account and emails a durable setup link to their
 * billing address. Idempotent for staff: call again to resend.
 */
export function useStartConnectOnboarding(resellerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post<PartnerConnectInviteResult>(`/api/resellers/${resellerId}/connect-onboarding`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['resellers'] }),
  });
}

export interface PartnerPortalInviteResult {
  invitationId: string;
  email: string;
  notified: boolean;
  renewed: boolean;
  expiresAt: string;
}

/**
 * Invites (or re-invites) somebody to administer this partner book in the portal.
 *
 * Distinct from Stripe Connect: that finishes payouts KYC for the organisation; this holds a
 * ResellerAdmin grant until they sign in.
 */
export function useSendPartnerPortalInvite(resellerId: string) {
  return useMutation({
    mutationFn: (input?: { email?: string; externalTenantId?: string }) =>
      api.post<PartnerPortalInviteResult>(`/api/resellers/${resellerId}/portal-invite`, input ?? {}),
  });
}

/**
 * Scopes the portal to what that partner's staff see — their customers only, no platform admin
 * surfaces. The session probe reflects the overlay; exit with {@link useExitViewAsPartner}.
 */
export function useViewAsPartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (resellerId: string) =>
      api.post<{ id: string; name: string }>(`/api/resellers/${resellerId}/view-as`),
    onSuccess: async () => {
      // Session first: nav and tenant list both branch on it. Tenants next so the list is already
      // filtered when the shell re-renders as a partner view.
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      await queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

/** Ends a view-as-partner overlay and restores the administrator's real grants. */
export function useExitViewAsPartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<void>('/api/auth/exit-view-as-partner'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      await queryClient.invalidateQueries({ queryKey: ['tenants'] });
      await queryClient.invalidateQueries({ queryKey: ['resellers'] });
    },
  });
}

/** Attributes a tenant to a partner, or detaches it when the id is null. */
export function useAttributeTenant(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { resellerId: string | null }) =>
      api.put<void>(`/api/tenants/${tenantId}/reseller`, input),
    onSuccess: async () => {
      // Awaited, like the other hooks in this file: attribution changes both what a partner owns
      // and which tenants a reseller-scoped session can reach, and a refetch nobody waited for is
      // a screen that redraws from stale data.
      await queryClient.invalidateQueries({ queryKey: ['resellers'] });
      await queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}
